import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Shield, PenTool, Eye, UserPlus, MessageCircle, Mail } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const AddUserModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    role: 'read_only',
    departments: [],
    password: '',
    can_send_messages: true,
    can_receive_messages: true,
    email_notifications: true
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const roles = [
    {
      value: 'read_only',
      label: 'Read Only',
      description: 'Can view content but cannot make changes',
      icon: Eye,
      color: 'text-gray-600'
    },
    {
      value: 'contributor',
      label: 'Contributor',
      description: 'Can create new content but cannot edit or delete existing content',
      icon: PenTool,
      color: 'text-blue-600'
    },
    {
      value: 'admin',
      label: 'Full Control',
      description: 'Full access including admin tasks and user management',
      icon: Shield,
      color: 'text-red-600'
    }
  ];

  const departments = [
    { value: 'product_development', label: 'Product Development' },
    { value: 'product_marketing', label: 'Product Marketing' },
    { value: 'sales', label: 'Sales' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDepartmentChange = (departmentValue, checked) => {
    setFormData(prev => ({
      ...prev,
      departments: checked 
        ? [...prev.departments, departmentValue]
        : prev.departments.filter(d => d !== departmentValue)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/users`, formData);
      
      toast({
        title: "Success",
        description: "User created successfully",
      });
      
      // Reset form
      setFormData({
        username: '',
        email: '',
        full_name: '',
        role: 'read_only',
        departments: [],
        password: ''
      });
      
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = roles.find(r => r.value === formData.role);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <span>Add New User</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="johndoe"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="john@company.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Leave empty for no password"
              />
              <p className="text-xs text-slate-500">
                Optional: User can set password later during first login
              </p>
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label>Permission Level *</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange('role', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center space-x-2">
                        <Icon className={`w-4 h-4 ${role.color}`} />
                        <span>{role.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {selectedRole && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center space-x-2 mb-1">
                  <selectedRole.icon className={`w-4 h-4 ${selectedRole.color}`} />
                  <span className="font-medium text-slate-700">{selectedRole.label}</span>
                </div>
                <p className="text-sm text-slate-600">{selectedRole.description}</p>
              </div>
            )}
          </div>

          {/* Department Access */}
          <div className="space-y-2">
            <Label>Department Access</Label>
            <div className="space-y-3">
              {departments.map((dept) => (
                <div key={dept.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={dept.value}
                    checked={formData.departments.includes(dept.value)}
                    onCheckedChange={(checked) => handleDepartmentChange(dept.value, checked)}
                  />
                  <Label htmlFor={dept.value} className="text-sm font-normal">
                    {dept.label}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Select which departments this user can access
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.username || !formData.email || !formData.full_name}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;